import { useState, useEffect, useRef } from 'react';
import { usePark } from '../contexts/ParkContext';
import axios from 'axios';
import { 
  Plus, 
  Search,
  DollarSign,
  Calendar,
  Filter,
  Download,
  Upload,
  Check,
  Trash2,
  FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Cuotas = () => {
  const { selectedPark, selectedYear } = usePark();
  const [fees, setFees] = useState([]);
  const [payments, setPayments] = useState([]);
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [filterMes, setFilterMes] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const fileInputRef = useRef(null);
  
  // State for bulk delete
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // State for bulk delete fees
  const [selectedFees, setSelectedFees] = useState([]);
  const [showDeleteFeesDialog, setShowDeleteFeesDialog] = useState(false);
  const [deletingFees, setDeletingFees] = useState(false);
  
  // State for manual payments
  const [manualPayments, setManualPayments] = useState([]);
  const [showManualPaymentModal, setShowManualPaymentModal] = useState(false);
  const [manualPaymentForm, setManualPaymentForm] = useState({
    lot_id: '',
    concepto: '',
    descripcion: '',
    fecha: new Date().toISOString().split('T')[0],
    monto_base: '',
    metodo_pago: 'transferencia',
    referencia: ''
  });

  // IVA rate constant
  const IVA_RATE = 0.16;

  const [paymentForm, setPaymentForm] = useState({
    lot_id: '',
    fecha: new Date().toISOString().split('T')[0],
    monto_total: '',  // Changed from monto to monto_total
    metodo_pago: 'transferencia',
    referencia: '',
    aplica_a_meses: [], // Changed to array for multiple months
    aplica_a_año: selectedYear
  });

  // Calculate IVA breakdown from total
  const calcularDesglose = (montoTotal) => {
    const total = parseFloat(montoTotal) || 0;
    const base = total / (1 + IVA_RATE);
    const iva = total - base;
    return { base: base.toFixed(2), iva: iva.toFixed(2), total: total.toFixed(2) };
  };

  const meses = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' }
  ];

  const metodosPago = [
    { value: 'transferencia', label: 'Transferencia' },
    { value: 'cheque', label: 'Cheque' },
    { value: 'efectivo', label: 'Efectivo' },
    { value: 'tarjeta', label: 'Tarjeta' },
    { value: 'otro', label: 'Otro' }
  ];

  useEffect(() => {
    if (selectedPark) {
      fetchData();
    }
  }, [selectedPark, selectedYear, filterMes, filterEstado]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let feesUrl = `${API}/maintenance-fees?park_id=${selectedPark.id}&año=${selectedYear}`;
      if (filterMes) feesUrl += `&mes=${filterMes}`;
      if (filterEstado) feesUrl += `&estado=${filterEstado}`;

      const [feesRes, paymentsRes, lotsRes, manualRes] = await Promise.all([
        axios.get(feesUrl),
        axios.get(`${API}/payments?park_id=${selectedPark.id}&año=${selectedYear}`),
        axios.get(`${API}/lots?park_id=${selectedPark.id}`),
        axios.get(`${API}/payments/manual?park_id=${selectedPark.id}&año=${selectedYear}`)
      ]);
      
      setFees(feesRes.data);
      setPayments(paymentsRes.data);
      setLots(lotsRes.data);
      setManualPayments(manualRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    
    if (paymentForm.aplica_a_meses.length === 0) {
      toast.error('Selecciona al menos un mes');
      return;
    }
    
    try {
      // If multiple months selected, create payment for each month (split evenly)
      const montoTotal = parseFloat(paymentForm.monto_total);
      const montoPorMes = montoTotal / paymentForm.aplica_a_meses.length;
      
      for (const mes of paymentForm.aplica_a_meses) {
        await axios.post(`${API}/payments`, {
          lot_id: paymentForm.lot_id,
          fecha: paymentForm.fecha,
          monto_total: Math.round(montoPorMes * 100) / 100, // Total depositado (incluye IVA)
          metodo_pago: paymentForm.metodo_pago,
          referencia: paymentForm.referencia,
          aplica_a_mes: parseInt(mes),
          aplica_a_año: parseInt(paymentForm.aplica_a_año)
        });
      }
      
      const mesesText = paymentForm.aplica_a_meses.length > 1 
        ? `${paymentForm.aplica_a_meses.length} meses` 
        : '1 mes';
      toast.success(`Pago registrado para ${mesesText}`);
      setShowPaymentModal(false);
      resetPaymentForm();
      fetchData();
    } catch (error) {
      toast.error('Error al registrar el pago');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('¿Estás seguro de eliminar este pago?')) return;
    
    try {
      await axios.delete(`${API}/payments/${paymentId}`);
      toast.success('Pago eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar el pago');
    }
  };

  // Bulk selection handlers
  const togglePaymentSelection = (paymentId) => {
    setSelectedPayments(prev => 
      prev.includes(paymentId) 
        ? prev.filter(id => id !== paymentId)
        : [...prev, paymentId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedPayments.length === payments.length) {
      setSelectedPayments([]);
    } else {
      setSelectedPayments(payments.map(p => p.id));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedPayments.length === 0) return;
    
    setDeleting(true);
    try {
      for (const id of selectedPayments) {
        await axios.delete(`${API}/payments/${id}`);
      }
      toast.success(`${selectedPayments.length} pagos eliminados`);
      setSelectedPayments([]);
      setShowDeleteDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar algunos pagos');
    } finally {
      setDeleting(false);
    }
  };

  // Handle bulk delete for fees
  const handleDeleteSelectedFees = async () => {
    if (selectedFees.length === 0) return;
    
    setDeletingFees(true);
    try {
      await axios.post(`${API}/maintenance-fees/massive-delete`, {
        fee_ids: selectedFees
      });
      toast.success(`${selectedFees.length} cuotas eliminadas`);
      setSelectedFees([]);
      setShowDeleteFeesDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar cuotas');
    } finally {
      setDeletingFees(false);
    }
  };

  // Toggle fee selection
  const toggleFeeSelection = (feeId) => {
    setSelectedFees(prev => 
      prev.includes(feeId) 
        ? prev.filter(id => id !== feeId)
        : [...prev, feeId]
    );
  };

  // Select all visible fees
  const toggleAllFees = () => {
    if (selectedFees.length === fees.length) {
      setSelectedFees([]);
    } else {
      setSelectedFees(fees.map(f => f.id));
    }
  };

  const resetPaymentForm = () => {
    setPaymentForm({
      lot_id: '',
      fecha: new Date().toISOString().split('T')[0],
      monto_total: '',
      metodo_pago: 'transferencia',
      referencia: '',
      aplica_a_meses: [],
      aplica_a_año: selectedYear
    });
  };

  const toggleMesSelection = (mesValue) => {
    const mes = mesValue.toString();
    setPaymentForm(prev => ({
      ...prev,
      aplica_a_meses: prev.aplica_a_meses.includes(mes)
        ? prev.aplica_a_meses.filter(m => m !== mes)
        : [...prev.aplica_a_meses, mes].sort((a, b) => parseInt(a) - parseInt(b))
    }));
  };

  const downloadCSVTemplate = () => {
    const template = `numero_lote,fecha,monto_total,metodo_pago,referencia,aplica_a_mes,aplica_a_año
L-001,2026-01-15,5800.00,transferencia,SPEI-123456,1,2026
L-001,2026-02-10,5800.00,cheque,CHQ-789,2,2026
L-002,2026-01-20,8700.00,transferencia,SPEI-456789,1,2026
L-003,2026-01-25,11600.00,efectivo,,1,2026`;
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plantilla_pagos.csv';
    a.click();
  };

  // Manual payment handlers
  const handleCreateManualPayment = async (e) => {
    e.preventDefault();
    
    const montoBase = parseFloat(manualPaymentForm.monto_base) || 0;
    const montoIva = montoBase * IVA_RATE;
    const montoTotal = montoBase + montoIva;
    
    try {
      await axios.post(`${API}/payments/manual`, {
        ...manualPaymentForm,
        monto_base: montoBase,
        monto_iva: parseFloat(montoIva.toFixed(2)),
        monto_total: parseFloat(montoTotal.toFixed(2))
      });
      
      toast.success('Pago registrado exitosamente');
      setShowManualPaymentModal(false);
      setManualPaymentForm({
        lot_id: '',
        concepto: '',
        descripcion: '',
        fecha: new Date().toISOString().split('T')[0],
        monto_base: '',
        metodo_pago: 'transferencia',
        referencia: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar el pago');
    }
  };

  const handleDeleteManualPayment = async (paymentId) => {
    if (!window.confirm('¿Estás seguro de eliminar este pago?')) return;
    
    try {
      await axios.delete(`${API}/payments/manual/${paymentId}`);
      toast.success('Pago eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar el pago');
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/payments/import-csv?park_id=${selectedPark.id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`Se importaron ${response.data.imported} pagos`);
      if (response.data.errors.length > 0) {
        response.data.errors.slice(0, 5).forEach(err => toast.error(err));
        if (response.data.errors.length > 5) {
          toast.error(`...y ${response.data.errors.length - 5} errores más`);
        }
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar el archivo');
    }
    
    e.target.value = '';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(value || 0);
  };

  const getStatusBadge = (estado) => {
    const badges = {
      pendiente: 'badge-pendiente',
      pagado: 'badge-pagado',
      parcial: 'badge-parcial',
      vencido: 'badge-vencido'
    };
    return badges[estado] || 'badge-pendiente';
  };

  const totalCuotas = fees.reduce((sum, f) => sum + (f.importe || 0), 0);
  const totalPagado = payments.reduce((sum, p) => sum + (p.monto || 0), 0);
  const totalPendiente = totalCuotas - totalPagado;

  if (!selectedPark) {
    return (
      <div className="animate-fadeIn">
        <div className="page-header">
          <h1>Cuotas e Ingresos</h1>
          <p>Selecciona un parque para ver las cuotas</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fadeIn">
      <div className="page-header">
        <h1 data-testid="cuotas-title">Cuotas e Ingresos</h1>
        <p>{selectedPark?.nombre} - Año {selectedYear}</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="card-industrial">
          <span className="label-overline">Cuotas Generadas</span>
          <p className="text-3xl font-black tracking-tight mt-2 tabular-nums" data-testid="total-cuotas">
            {formatCurrency(totalCuotas)}
          </p>
        </div>
        <div className="card-industrial">
          <span className="label-overline">Total Cobrado</span>
          <p className="text-3xl font-black tracking-tight mt-2 tabular-nums text-emerald-600" data-testid="total-pagado">
            {formatCurrency(totalPagado)}
          </p>
        </div>
        <div className="card-industrial">
          <span className="label-overline">Pendiente de Cobro</span>
          <p className="text-3xl font-black tracking-tight mt-2 tabular-nums text-amber-600" data-testid="total-pendiente">
            {formatCurrency(totalPendiente)}
          </p>
        </div>
      </div>

      <Tabs defaultValue="cuotas" className="space-y-6">
        <TabsList className="bg-slate-100 p-1 rounded-none">
          <TabsTrigger value="cuotas" className="rounded-none data-[state=active]:bg-white" data-testid="tab-cuotas">
            Cuotas Generadas
          </TabsTrigger>
          <TabsTrigger value="pagos" className="rounded-none data-[state=active]:bg-white" data-testid="tab-pagos">
            Pagos Recibidos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cuotas">
          {/* Action Bar for Fees */}
          <div className="action-bar mb-4">
            <span className="text-slate-500">{fees.length} cuotas generadas</span>
            <div className="flex items-center gap-3">
              {selectedFees.length > 0 && (
                <Button
                  variant="destructive"
                  className="rounded-none"
                  onClick={() => setShowDeleteFeesDialog(true)}
                  disabled={deletingFees}
                  data-testid="delete-selected-fees-btn"
                >
                  <Trash2 size={16} className="mr-2" />
                  {deletingFees ? 'Eliminando...' : `Eliminar (${selectedFees.length})`}
                </Button>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="filter-bar">
            <div className="flex items-center gap-2 text-slate-500">
              <Filter size={18} />
              <span className="text-sm font-medium">Filtros:</span>
            </div>
            <Select value={filterMes || "all"} onValueChange={(v) => setFilterMes(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40 input-industrial" data-testid="filter-mes">
                <SelectValue placeholder="Todos los meses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los meses</SelectItem>
                {meses.map(m => (
                  <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterEstado || "all"} onValueChange={(v) => setFilterEstado(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40 input-industrial" data-testid="filter-estado">
                <SelectValue placeholder="Todos los estados" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="pendiente">Pendiente</SelectItem>
                <SelectItem value="parcial">Parcial</SelectItem>
                <SelectItem value="pagado">Pagado</SelectItem>
                <SelectItem value="vencido">Vencido</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Fees Table */}
          {loading ? (
            <div className="card-industrial animate-pulse">
              <div className="h-64 bg-slate-100"></div>
            </div>
          ) : fees.length === 0 ? (
            <div className="empty-state py-16 card-industrial">
              <div className="empty-state-icon">
                <Calendar size={24} className="text-slate-400" />
              </div>
              <h3>No hay cuotas generadas</h3>
              <p>Genera las cuotas del año desde el módulo de Presupuesto.</p>
            </div>
          ) : (
            <div className="card-industrial overflow-hidden p-0">
              <div className="overflow-x-auto">
                <table className="table-blueprint" data-testid="fees-table">
                  <thead>
                    <tr>
                      <th className="w-10">
                        <Checkbox
                          checked={fees.length > 0 && selectedFees.length === fees.length}
                          onCheckedChange={toggleAllFees}
                          aria-label="Seleccionar todas"
                        />
                      </th>
                      <th>Lote</th>
                      <th>Mes</th>
                      <th>Tipo</th>
                      <th className="text-right">Base</th>
                      <th className="text-right">IVA (16%)</th>
                      <th className="text-right">Total</th>
                      <th>Vencimiento</th>
                      <th>Estado</th>
                      <th>Propietario</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fees.map((fee, index) => (
                      <tr 
                        key={fee.id} 
                        className={`animate-slideIn ${selectedFees.includes(fee.id) ? 'bg-amber-50' : ''}`} 
                        style={{ animationDelay: `${index * 20}ms` }}
                      >
                        <td>
                          <Checkbox
                            checked={selectedFees.includes(fee.id)}
                            onCheckedChange={() => toggleFeeSelection(fee.id)}
                            aria-label={`Seleccionar cuota ${fee.numero_lote} - ${meses.find(m => m.value === fee.mes)?.label}`}
                          />
                        </td>
                        <td className="font-mono font-medium">{fee.numero_lote}</td>
                        <td>{meses.find(m => m.value === fee.mes)?.label}</td>
                        <td>
                          {fee.tipo_cuota === 'extraordinario' ? (
                            <span className="inline-block px-2 py-0.5 text-xs font-medium rounded bg-amber-100 text-amber-800" title={fee.concepto_extra || ''}>
                              Extra
                            </span>
                          ) : (
                            <span className="inline-block px-2 py-0.5 text-xs font-medium rounded bg-slate-100 text-slate-600">
                              Ord.
                            </span>
                          )}
                        </td>
                        <td className="text-right tabular-nums">{formatCurrency(fee.importe_base || fee.importe)}</td>
                        <td className="text-right tabular-nums text-slate-500">{formatCurrency(fee.importe_iva || (fee.importe * IVA_RATE))}</td>
                        <td className="text-right tabular-nums font-medium">{formatCurrency(fee.importe_total || (fee.importe * (1 + IVA_RATE)))}</td>
                        <td className="font-mono text-sm">{fee.fecha_vencimiento}</td>
                        <td>
                          <span className={`inline-block px-2 py-1 text-xs font-medium capitalize ${getStatusBadge(fee.estado)}`}>
                            {fee.estado}
                          </span>
                        </td>
                        <td className="text-slate-500">{fee.propietario_nombre || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="pagos">
          {/* Action Bar */}
          <div className="action-bar">
            <span className="text-slate-500">{payments.length} pagos registrados</span>
            <div className="flex items-center gap-3">
              {selectedPayments.length > 0 && (
                <Button
                  variant="destructive"
                  className="rounded-none"
                  onClick={() => setShowDeleteDialog(true)}
                  disabled={deleting}
                  data-testid="delete-selected-payments-btn"
                >
                  <Trash2 size={16} className="mr-2" />
                  {deleting ? 'Eliminando...' : `Eliminar (${selectedPayments.length})`}
                </Button>
              )}
              <Button
                variant="outline"
                className="rounded-none"
                onClick={downloadCSVTemplate}
                data-testid="download-payments-template-btn"
              >
                <Download size={16} className="mr-2" />
                Plantilla CSV
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleImportCSV}
                className="hidden"
              />
              <Button
                variant="outline"
                className="rounded-none"
                onClick={() => fileInputRef.current?.click()}
                data-testid="import-payments-csv-btn"
              >
                <Upload size={16} className="mr-2" />
                Importar CSV
              </Button>
              <Button 
                className="btn-industrial bg-slate-900 text-white hover:bg-slate-800"
                onClick={() => { resetPaymentForm(); setShowPaymentModal(true); }}
                data-testid="add-payment-btn"
              >
                <Plus size={16} className="mr-2" />
                Registrar Pago
              </Button>
              <Button 
                variant="outline"
                className="rounded-none border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                onClick={() => setShowManualPaymentModal(true)}
                data-testid="add-manual-payment-btn"
              >
                <FileText size={16} className="mr-2" />
                Otro Ingreso
              </Button>
            </div>
          </div>

          {/* Payments Table */}
          {loading ? (
            <div className="card-industrial animate-pulse">
              <div className="h-64 bg-slate-100"></div>
            </div>
          ) : payments.length === 0 ? (
            <div className="empty-state py-16 card-industrial">
              <div className="empty-state-icon">
                <DollarSign size={24} className="text-slate-400" />
              </div>
              <h3>No hay pagos registrados</h3>
              <p>Comienza registrando los pagos recibidos.</p>
            </div>
          ) : (
            <div className="card-industrial overflow-hidden p-0">
              <div className="overflow-x-auto">
                <table className="table-blueprint" data-testid="payments-table">
                  <thead>
                    <tr>
                      <th className="w-12">
                        <Checkbox
                          checked={selectedPayments.length === payments.length && payments.length > 0}
                          onCheckedChange={toggleSelectAll}
                          data-testid="select-all-payments"
                        />
                      </th>
                      <th>Fecha</th>
                      <th>Lote</th>
                      <th>Periodo</th>
                      <th className="text-right">Base</th>
                      <th className="text-right">IVA</th>
                      <th className="text-right">Total</th>
                      <th>Método</th>
                      <th>Referencia</th>
                      <th className="w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment, index) => (
                      <tr 
                        key={payment.id} 
                        className={`animate-slideIn ${selectedPayments.includes(payment.id) ? 'bg-amber-50' : ''}`} 
                        style={{ animationDelay: `${index * 20}ms` }}
                      >
                        <td>
                          <Checkbox
                            checked={selectedPayments.includes(payment.id)}
                            onCheckedChange={() => togglePaymentSelection(payment.id)}
                            data-testid={`select-payment-${payment.id}`}
                          />
                        </td>
                        <td className="font-mono text-sm">{payment.fecha}</td>
                        <td className="font-mono font-medium">{payment.numero_lote}</td>
                        <td>{meses.find(m => m.value === payment.aplica_a_mes)?.label} {payment.aplica_a_año}</td>
                        <td className="text-right tabular-nums">{formatCurrency(payment.monto_base || payment.monto)}</td>
                        <td className="text-right tabular-nums text-slate-500">{formatCurrency(payment.monto_iva || (payment.monto * IVA_RATE))}</td>
                        <td className="text-right tabular-nums font-medium text-emerald-600">{formatCurrency(payment.monto_total || (payment.monto * (1 + IVA_RATE)))}</td>
                        <td className="capitalize">{payment.metodo_pago}</td>
                        <td className="text-slate-500 text-sm">{payment.referencia || '-'}</td>
                        <td>
                          <button
                            onClick={() => handleDeletePayment(payment.id)}
                            className="p-2 hover:bg-red-50 transition-colors text-red-500"
                            data-testid={`delete-payment-${payment.id}`}
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Manual Payments Section */}
          {manualPayments.length > 0 && (
            <div className="mt-8">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <FileText size={20} />
                Otros Ingresos
              </h3>
              <div className="card-industrial overflow-hidden p-0">
                <div className="overflow-x-auto">
                  <table className="table-blueprint" data-testid="manual-payments-table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Lote</th>
                        <th>Concepto</th>
                        <th>Descripción</th>
                        <th className="text-right">Base</th>
                        <th className="text-right">IVA</th>
                        <th className="text-right">Total</th>
                        <th>Método</th>
                        <th>Referencia</th>
                        <th className="w-16"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {manualPayments.map((payment, index) => (
                        <tr key={payment.id} className="animate-slideIn" style={{ animationDelay: `${index * 20}ms` }}>
                          <td className="font-mono text-sm">{payment.fecha}</td>
                          <td className="font-mono font-medium">{payment.numero_lote}</td>
                          <td className="font-medium text-emerald-600">{payment.concepto}</td>
                          <td className="text-slate-500 text-sm max-w-xs truncate">{payment.descripcion || '-'}</td>
                          <td className="text-right tabular-nums">{formatCurrency(payment.monto_base)}</td>
                          <td className="text-right tabular-nums text-slate-500">{formatCurrency(payment.monto_iva)}</td>
                          <td className="text-right tabular-nums font-medium text-emerald-600">{formatCurrency(payment.monto_total)}</td>
                          <td className="capitalize">{payment.metodo_pago}</td>
                          <td className="text-slate-500 text-sm">{payment.referencia || '-'}</td>
                          <td>
                            <button
                              onClick={() => handleDeleteManualPayment(payment.id)}
                              className="p-2 hover:bg-red-50 transition-colors text-red-500"
                              data-testid={`delete-manual-payment-${payment.id}`}
                            >
                              ×
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog for Payments */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar pagos seleccionados?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminarán {selectedPayments.length} pagos permanentemente. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSelected}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog for Fees */}
      <AlertDialog open={showDeleteFeesDialog} onOpenChange={setShowDeleteFeesDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar cuotas seleccionadas?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminarán {selectedFees.length} cuotas permanentemente. Esta acción no se puede deshacer.
              Las cuotas con pagos asociados no se eliminarán.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingFees}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSelectedFees}
              disabled={deletingFees}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingFees ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Registrar Pago</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePayment}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="label-overline">Lote</Label>
                <Select
                  value={paymentForm.lot_id}
                  onValueChange={(value) => setPaymentForm({ ...paymentForm, lot_id: value })}
                >
                  <SelectTrigger className="input-industrial" data-testid="payment-lot-select">
                    <SelectValue placeholder="Seleccionar lote..." />
                  </SelectTrigger>
                  <SelectContent>
                    {lots.map(lot => (
                      <SelectItem key={lot.id} value={lot.id}>{lot.numero_lote}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-overline">Fecha de Pago</Label>
                  <Input
                    type="date"
                    value={paymentForm.fecha}
                    onChange={(e) => setPaymentForm({ ...paymentForm, fecha: e.target.value })}
                    required
                    className="input-industrial"
                    data-testid="payment-fecha-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Monto Total Depositado</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={paymentForm.monto_total}
                    onChange={(e) => setPaymentForm({ ...paymentForm, monto_total: e.target.value })}
                    placeholder="0.00"
                    required
                    className="input-industrial"
                    data-testid="payment-monto-input"
                  />
                </div>
              </div>
              
              {/* IVA Breakdown Display */}
              {paymentForm.monto_total && parseFloat(paymentForm.monto_total) > 0 && (
                <div className="bg-slate-50 border border-slate-200 p-3 text-sm space-y-1">
                  <p className="font-medium text-slate-700 mb-2">Desglose automático:</p>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Cuota (Base):</span>
                    <span className="tabular-nums font-medium">{formatCurrency(calcularDesglose(paymentForm.monto_total).base)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">IVA (16%):</span>
                    <span className="tabular-nums font-medium">{formatCurrency(calcularDesglose(paymentForm.monto_total).iva)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-1 mt-1">
                    <span className="font-medium">Total:</span>
                    <span className="tabular-nums font-bold text-emerald-600">{formatCurrency(calcularDesglose(paymentForm.monto_total).total)}</span>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-overline">Aplica a Mes(es)</Label>
                  <div className="grid grid-cols-4 gap-1 p-2 border border-slate-200 rounded bg-white max-h-32 overflow-y-auto">
                    {meses.map(m => (
                      <button
                        key={m.value}
                        type="button"
                        onClick={() => toggleMesSelection(m.value)}
                        className={`px-2 py-1 text-xs font-medium rounded transition-colors flex items-center justify-center gap-1
                          ${paymentForm.aplica_a_meses.includes(m.value.toString())
                            ? 'bg-slate-900 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        data-testid={`payment-mes-${m.value}`}
                      >
                        {paymentForm.aplica_a_meses.includes(m.value.toString()) && (
                          <Check size={10} />
                        )}
                        {m.label.substring(0, 3)}
                      </button>
                    ))}
                  </div>
                  {paymentForm.aplica_a_meses.length > 1 && (
                    <p className="text-xs text-amber-600">
                      El monto se dividirá en {paymentForm.aplica_a_meses.length} pagos iguales
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Año</Label>
                  <Input
                    type="number"
                    value={paymentForm.aplica_a_año}
                    onChange={(e) => setPaymentForm({ ...paymentForm, aplica_a_año: e.target.value })}
                    required
                    className="input-industrial"
                    data-testid="payment-year-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-overline">Método de Pago</Label>
                  <Select
                    value={paymentForm.metodo_pago}
                    onValueChange={(value) => setPaymentForm({ ...paymentForm, metodo_pago: value })}
                  >
                    <SelectTrigger className="input-industrial" data-testid="payment-metodo-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {metodosPago.map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Referencia</Label>
                  <Input
                    value={paymentForm.referencia}
                    onChange={(e) => setPaymentForm({ ...paymentForm, referencia: e.target.value })}
                    placeholder="No. de cheque, folio..."
                    className="input-industrial"
                    data-testid="payment-referencia-input"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowPaymentModal(false)} className="rounded-none">
                Cancelar
              </Button>
              <Button type="submit" className="btn-industrial bg-slate-900 text-white hover:bg-slate-800" data-testid="save-payment-btn">
                Registrar Pago
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manual Payment Modal */}
      <Dialog open={showManualPaymentModal} onOpenChange={setShowManualPaymentModal}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Registrar Otro Ingreso</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateManualPayment}>
            <div className="space-y-4 py-4">
              <div>
                <Label>Lote *</Label>
                <Select 
                  value={manualPaymentForm.lot_id} 
                  onValueChange={(val) => setManualPaymentForm(prev => ({ ...prev, lot_id: val }))}
                  required
                >
                  <SelectTrigger className="rounded-none" data-testid="manual-payment-lot-select">
                    <SelectValue placeholder="Seleccionar lote" />
                  </SelectTrigger>
                  <SelectContent>
                    {lots.map(lot => (
                      <SelectItem key={lot.id} value={lot.id}>
                        {lot.numero_lote} - {lot.propietario_nombre || 'Sin propietario'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Concepto *</Label>
                <Select 
                  value={manualPaymentForm.concepto} 
                  onValueChange={(val) => setManualPaymentForm(prev => ({ ...prev, concepto: val }))}
                  required
                >
                  <SelectTrigger className="rounded-none" data-testid="manual-payment-concepto-select">
                    <SelectValue placeholder="Seleccionar concepto" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Agua">Agua</SelectItem>
                    <SelectItem value="Tags de acceso">Tags de acceso</SelectItem>
                    <SelectItem value="Préstamo">Préstamo</SelectItem>
                    <SelectItem value="Multa">Multa</SelectItem>
                    <SelectItem value="Reparaciones">Reparaciones</SelectItem>
                    <SelectItem value="Publicidad">Publicidad</SelectItem>
                    <SelectItem value="Otro">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Descripción (opcional)</Label>
                <Textarea
                  value={manualPaymentForm.descripcion}
                  onChange={(e) => setManualPaymentForm(prev => ({ ...prev, descripcion: e.target.value }))}
                  placeholder="Detalle del ingreso..."
                  rows={2}
                  className="rounded-none resize-none"
                  data-testid="manual-payment-descripcion"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha *</Label>
                  <Input
                    type="date"
                    value={manualPaymentForm.fecha}
                    onChange={(e) => setManualPaymentForm(prev => ({ ...prev, fecha: e.target.value }))}
                    required
                    className="rounded-none"
                    data-testid="manual-payment-fecha"
                  />
                </div>
                <div>
                  <Label>Monto (sin IVA) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={manualPaymentForm.monto_base}
                    onChange={(e) => setManualPaymentForm(prev => ({ ...prev, monto_base: e.target.value }))}
                    placeholder="0.00"
                    required
                    className="rounded-none"
                    data-testid="manual-payment-monto"
                  />
                </div>
              </div>

              {/* Amount preview */}
              {manualPaymentForm.monto_base && (
                <div className="bg-emerald-50 p-3 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span className="text-emerald-700">Subtotal:</span>
                    <span>{formatCurrency(parseFloat(manualPaymentForm.monto_base) || 0)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-emerald-700">IVA (16%):</span>
                    <span>{formatCurrency((parseFloat(manualPaymentForm.monto_base) || 0) * IVA_RATE)}</span>
                  </div>
                  <div className="flex justify-between font-bold mt-1 pt-1 border-t border-emerald-200">
                    <span className="text-emerald-700">Total:</span>
                    <span className="text-emerald-700">
                      {formatCurrency((parseFloat(manualPaymentForm.monto_base) || 0) * (1 + IVA_RATE))}
                    </span>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Método de Pago *</Label>
                  <Select 
                    value={manualPaymentForm.metodo_pago} 
                    onValueChange={(val) => setManualPaymentForm(prev => ({ ...prev, metodo_pago: val }))}
                  >
                    <SelectTrigger className="rounded-none" data-testid="manual-payment-metodo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="transferencia">Transferencia</SelectItem>
                      <SelectItem value="efectivo">Efectivo</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                      <SelectItem value="tarjeta">Tarjeta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Referencia (opcional)</Label>
                  <Input
                    value={manualPaymentForm.referencia}
                    onChange={(e) => setManualPaymentForm(prev => ({ ...prev, referencia: e.target.value }))}
                    placeholder="No. transferencia"
                    className="rounded-none"
                    data-testid="manual-payment-referencia"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowManualPaymentModal(false)} className="rounded-none">
                Cancelar
              </Button>
              <Button type="submit" className="btn-industrial bg-emerald-600 text-white hover:bg-emerald-700" data-testid="save-manual-payment-btn">
                <FileText size={16} className="mr-2" />
                Registrar Ingreso
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Cuotas;
